import { Grid, Paper, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useI18n } from '../../../../../../../application/language/useI18n';
import { ContainerContext } from '../../../../../../../common/container/ContainerContext';
import { ICommonProperties } from '../../../../../../../common/properties/ICommonProperties';
import { observer } from 'mobx-react';
import React, { useContext, useEffect } from 'react';

import { SpecificationFormSelection } from '../../../../../../admin/page/specification/parts/formSelection/SpecificationFormSelection';
import { FormAutocomplete } from '../../../../../../design/form/FormAutocomplete';
import { isAcceptRequirementAvailableExtension } from '../../inject/isAcceptRequirementAvailableExtension';
import { ApplicationDetailDomain } from '../../store/ApplicationDetailDomain';
import { ApplicationDetailsType } from '../../store/ApplicationDetailsType';

export interface IApplicationInfoDetailsFormProperties extends ICommonProperties {
  domain: ApplicationDetailDomain;
}

export const ApplicationInfoDetailsForm = observer(({ domain }: IApplicationInfoDetailsFormProperties) => {
  const container = useContext(ContainerContext);
  const classes = useStyles();
  const isNew = !domain.ui.editApplication.entity.id;
  const isEntityType = domain.ui.type.value === ApplicationDetailsType.entity;

  const QuestionnaireEditExtended = container.get(QuestionnaireEdit);

  const isAcceptRequirementAvailableExtensionHandlers = container.get(isAcceptRequirementAvailableExtension);
  const isAcceptRequirementAvailableExtensionValues = isAcceptRequirementAvailableExtensionHandlers.map((handler) =>
    handler(domain),
  );
  const isAcceptRequirementAvailable =
    isNew && isEntityType && !isAcceptRequirementAvailableExtensionValues.includes(false);

  const performersList = domain.ui.performerTypesAutocompleteValues();
  if (performersList.length === 1) {
    domain.ui.editApplication.entity.performerTypeId = performersList[0].value;
  }

  useEffect(() => {
    if (domain.projectFormDomain) {
      domain.syncProjectAndApplicationDomains();
    }
  }, []);

  return (
    <Paper className={classes.root} elevation={3}>
      <div className={classes.formContent}>
        <Grid container direction={'column'} spacing={4}>
          <Grid style={{ ...(performersList.length === 1 && { display: 'none' }) }} item xs={6}>
            {performersList.length > 0 && domain.ui.editApplication.entity && (
              <PerformerInput
                list={performersList}
                domain={domain}
                errorMessage={domain.getApplicationValidationErrorFor('performerTypeId')?.message}
                onChange={domain.getUpdateSelectFieldHandler('performerTypeId')}
              />
            )}
          </Grid>

          <QuestionnaireEditExtended domain={domain} />

          {isAcceptRequirementAvailable && <></>}
        </Grid>
      </div>
    </Paper>
  );
});

const PerformerInput = observer(({ domain, list, errorMessage, onChange, style }: any) => {
  const { translate } = useI18n();

  return (
    <FormAutocomplete
      style={style}
      errorMessage={errorMessage}
      values={list}
      onChangeSelect={onChange}
      key="performerTypeId"
      label={translate('projectPage.typeOfExecutors')}
      required={true}
      helperText={translate('projectPage.typeOfExecutorsHelperText')}
      selected={domain.ui.editApplication.entity.performerTypeId || null}
      selectedDefault={null}
    />
  );
});

export const QuestionnaireEdit = observer(({ domain }: IApplicationInfoDetailsFormProperties) => {
  return (
    <>
      <Grid item xs={6}>
        <SpecificationFormSelection
          onChange={(specificationIds) => domain.onChangeApplicationSpecifications(specificationIds)}
          selectedSpecificationsIds={domain.ui.editApplication.entity.specificationsIds || []}
          specificationCategories={domain.ui.editSpecificationsCategories.list}
          specifications={domain.ui.editSpecifications.list}
          applicationDetailDomain={domain}
          componentFromType={'fromApplicationCreate'}
        />
      </Grid>
    </>
  );
});

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(6),
    paddingLeft: theme.spacing(6),
    paddingBottom: theme.spacing(6),
    paddingRight: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: 'transparent',
  },
  formContent: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
    paddingRight: theme.spacing(6),
  },
}));
